/**
 * „Produkte, letzte 12 Monate" (P1-V3 § 2.4, Umbau 25.10.2026): je Produkt Anfos und der Erwartete Wert (Summe · Ø je
 * Anfo) — nach Wert sortiert —, dazu „Über Webinare", Bezahlquote mit ▲/▼ und das letzte Webinar; weitere Spalten über
 * die Spaltenwahl. Dieselbe Rechnung wie die P3-Kachel (`kennzahlenSql`/`kennzahlenAus`/`markiereBezahlquote`, Test 17).
 * Einmalkäufe und ausgelaufene Produkte stehen in einem Abschnitt am Ende (Konstanten, keine Zeitregel).
 */
import { FC, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import {
  AbweichungMarke,
  BefundZeile,
  BezahltBuendel,
  datenstandSatz,
  fest,
  formatDelta,
  nachGruppenReihenfolge,
  spanne,
  tEuro,
  veraenderungTip,
  VeraenderungWert,
} from '../../../components/auswertungen/kennzahlTeile';
import { Legende, TipRow, useVizStyle, achse } from '../../../components/auswertungen/vizStyle';
import { ATTRIBUTION, formatEur, formatPct, spaltenTip, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchProdukte, fetchWebinarRankingGeteilt, ProdukteZeitraum, ProduktZeile } from '../../../helpers/auswertungenData';
import { AnfosMitVorlaeufig, balkenHoehe, datum, GeschaetztMarke, Hinweis, int, Laden, NamensEtikett, ProduktZelle, Strich, tagMitWochentag, tagMonat, Unter, useLaden, useLadenMit, vorlaeufigSatz } from './shared';
import { vorWochen } from './empfehlungText';
import { produkteBefund, produktLabel } from './befunde';

/** Achsenname in höchstens zwei Zeilen: an „ - " getrennt (Name / Zusatz), sonst an Wortgrenzen zu ~16 Zeichen; Rest „…". */
function zeilen(text: string, max = 16): string[] {
  if (text.length <= max) return [text];
  const teil = text.split(' - ');
  if (teil.length === 2) return teil.map((z) => (z.length > max + 6 ? `${z.slice(0, max + 5)}…` : z));
  const woerter = text.split(/\s+/);
  const out: string[] = [''];
  for (const w of woerter) {
    const z = out[out.length - 1];
    if (!z) out[out.length - 1] = w;
    else if (`${z} ${w}`.length <= max) out[out.length - 1] = `${z} ${w}`;
    else out.push(w);
  }
  if (out.length <= 2) return out.map((z) => (z.length > max + 4 ? `${z.slice(0, max + 3)}…` : z));
  const zweite = out.slice(1).join(' ');
  return [out[0], zweite.length > max ? `${zweite.slice(0, max - 1)}…` : zweite];
}

/**
 * Achsen-Kürzel am Handy (Audit D9, unter `sm`): das Produktkürzel (TAM); bei reinen Artikelnummern der Name ohne
 * Artikel, auf 10 Zeichen gekürzt. Der volle Name steht im `<title>` und in der Tabelle.
 */
export function achsenKuerzel(code: string, name: string | null | undefined): string {
  if (!/^\d/.test(code) || !name) return code;
  const n = name.replace(/^(der|die|das)\s+/i, '').trim();
  return n.length > 10 ? `${n.slice(0, 9).trimEnd()}…` : n;
}

/** Achsenbeschriftung links (B5): Name statt Artikelnummer, zweizeilig; Tick-Position wie Nivo. */
const AchsenName: FC<{ tick: { x: number; y: number; textX: number; textY: number }; text: string; voll?: string; farbe: string; fontFamily: string }> = ({
  tick,
  text,
  voll,
  farbe,
  fontFamily,
}) => {
  const z = zeilen(text);
  return (
    <g transform={`translate(${tick.x},${tick.y})`}>
      <text textAnchor="end" dominantBaseline="central" fontSize={12} fontFamily={fontFamily} fill={farbe} transform={`translate(${tick.textX},${tick.textY})`}>
        {z.map((zeile, i) => (
          <tspan key={i} x={0} dy={i === 0 ? (z.length > 1 ? '-0.55em' : 0) : '1.15em'}>
            {zeile}
          </tspan>
        ))}
        <title>{voll ?? text}</title>
      </text>
    </g>
  );
};

const GRUPPEN = ['Wert', 'Menge', 'Trend', 'Kundentyp', 'Zahlung (wächst mit dem Alter)', 'Planung'] as const;
const STANDARDS: Record<Breite, readonly string[]> = {
  // 'preis' (Ø Listenpreis) gibt es nur außerhalb von 12 Monaten (`passt`); dort ist er Standard und abwählbar (schmal
  // nicht: eine vierte Spalte ließ die Tabelle bei 390 px quer scrollen, Abnahme 29.10.).
  breit: ['produkt', 'anfos', 'wert', 'ueber', 'quote', 'letztes', 'preis'],
  mittel: ['produkt', 'anfos', 'wert', 'quote', 'letztes', 'preis'],
  schmal: ['produkt', 'anfos', 'wert'],
};
/** Ab diesem Anteil Upsell (Jahresbezug, Zahlung vorab) erklärt der Tooltip eine hohe Bezahlquote. */
const VORAB_ANTEIL = 0.25;

/** Alle Anfos vorläufig ergänzt (z. B. DKD Einmalfaktura): keine Quote und kein Wert möglich. */
const nurVorlaeufig = (r: Pick<ProduktZeile, 'anfos' | 'provisional'>) => r.anfos > 0 && r.provisional === r.anfos;
const imAbschnitt = (r: ProduktZeile) => !!r.ist_einmalkauf || !!r.nachfolger;
/** Anfos, aber keine davon bewertet: Erwarteter Wert unbekannt (nie als 0 € zeigen). */
const ohneWert = (r: Pick<ProduktZeile, 'anfos' | 'provisional' | 'with_value'>) => r.anfos - r.provisional > 0 && (r.with_value ?? 0) === 0;
const mmjj = (d: string) => `${d.slice(5, 7)}/${d.slice(2, 4)}`;

/** Workshop E3 (29.10.2026): Zeitraum-Umschalter, Titel je Zeitraum. */
const ZEITRAEUME: { key: ProdukteZeitraum; label: string; titel: string; satz: string }[] = [
  { key: '12m', label: '12 Monate', titel: 'Produkte, letzte 12 Monate', satz: 'letzte 12 Monate' },
  { key: '2025', label: 'seit 2025', titel: 'Produkte seit 2025', satz: 'seit 01.01.2025' },
  { key: 'gesamt', label: 'gesamt', titel: 'Produkte seit Beginn der Daten', satz: 'seit der ersten Anfo (25.01.2024)' },
];

const ProdukteKarte: FC = () => {
  const [zeitraum, setZeitraum] = useState<ProdukteZeitraum>('12m');
  const zwoelf = zeitraum === '12m';
  const z = ZEITRAEUME.find((x) => x.key === zeitraum) ?? ZEITRAEUME[0];
  const { data, fehler } = useLadenMit(fetchProdukte, zeitraum);
  // Etiketten „geplant"/„frei ab" und Bestandskunden aus der geteilten Ranking-Antwort (kein eigener Abruf).
  const ranking = useLaden(fetchWebinarRankingGeteilt);
  const viz = useVizStyle();
  const [ansicht, setAnsicht] = useState<'wert' | 'anfos'>('wert');

  const plan = useMemo(() => {
    const p = ranking.data?.prognose;
    const geplant = new Map<string, string>();
    for (const g of p?.geplant ?? []) if (g.plx_product) geplant.set(g.plx_product, p?.zieltermin ?? '');
    const frei = new Map((p?.neue_konzepte ?? []).filter((n) => n.frei_ab).map((n) => [n.plx_product, n.frei_ab as string]));
    const bestand = new Map<string, number | null>();
    for (const k of p?.kandidaten ?? []) if (k.plx_product && !bestand.has(k.plx_product)) bestand.set(k.plx_product, k.bestandskunden);
    return { geplant, frei, bestand };
  }, [ranking.data]);

  const chartData = useMemo(
    () =>
      (data?.rows ?? [])
        .filter((r) => (ansicht === 'wert' ? (r.value_sum ?? 0) > 0 : r.anfos > 0))
        .slice()
        .sort((a, b) => (ansicht === 'wert' ? (a.value_sum ?? 0) - (b.value_sum ?? 0) : a.anfos - b.anfos))
        .map((r) => {
          const web = ansicht === 'wert' ? (r.webinar_value_sum ?? 0) : r.webinar_anfos;
          const gesamt = ansicht === 'wert' ? (r.value_sum ?? 0) : r.anfos;
          // B5: Artikelnummern (2010006) zeigen ihren Namen auf der Achse; `produkt` bleibt der Schlüssel.
          return { produkt: r.plx_product || '(ohne)', achse: produktLabel(r.plx_product || '(ohne)', r.name), name: r.name, webinar: web, andere: Math.max(0, gesamt - web), gesamt };
        }),
    [data, ansicht],
  );

  const handy = !useMediaQuery(useTheme().breakpoints.up('sm'));
  const achsenText = useMemo(() => new Map(chartData.map((d) => [d.produkt, handy ? achsenKuerzel(d.produkt, d.name) : d.achse])), [chartData, handy]);
  const achsenVoll = useMemo(() => new Map(chartData.map((d) => [d.produkt, d.achse])), [chartData]);
  // Linker Rand nach der längsten Achsenzeile (Namen brechen in höchstens zwei Zeilen um), 72–150 px.
  // Schmal (< md): höchstens 120 px links, rechts 56 px, zwei Achsenwerte — bei 390 px blieben mit 150 + 72 px nur ~45 px
  // Zeichenfläche, die fünf Achsenwerte lagen übereinander (Abnahme 29.10., Zeitraum „seit 2025“ mit langen Namen).
  const breitBild = useMediaQuery(useTheme().breakpoints.up('md'));
  const links = useMemo(
    () =>
      Math.min(
        breitBild ? 150 : 120,
        Math.max(handy ? 40 : 72, ...chartData.map((d) => Math.max(...zeilen(achsenText.get(d.produkt) ?? d.achse).map((z) => z.length)) * 6.6 + 14)),
      ),
    [chartData, breitBild, handy, achsenText],
  );
  const s = data?.summary;
  const schwelle = data?.grau_schwellen?.veraenderung_anfos ?? 20;
  const befund = data ? produkteBefund(data.rows, s?.value_per_anfo ?? null) : null;
  const baue = (breit = true): Spalte<ProduktZeile>[] => [
    {
      key: 'produkt',
      label: 'Produkt',
      gruppe: 'Wert',
      pflicht: true,
      minWidth: breit ? 150 : undefined,
      render: (r) => <ProduktZelle code={r.plx_product || '(ohne Artikel)'} name={r.name} />,
      sortValue: (r) => r.plx_product,
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      gruppe: 'Menge',
      pflicht: true,
      align: 'right',
      render: (r) => <AnfosMitVorlaeufig n={r.anfos} vorlaeufig={r.provisional} />,
      sortValue: (r) => r.anfos,
      summary: s ? <AnfosMitVorlaeufig n={s.anfos} vorlaeufig={s.provisional} /> : null,
    },
    {
      key: 'wert',
      label: TERMS.wertSumme.label,
      einheit: 'Summe · Ø je Anfo',
      tip: TERMS.wertBeiBestellung.tip,
      hinweis: 'Ohne vorläufig ergänzte Anfos. Über das Alter vergleichbar — anders als „bisher bezahlt“.',
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      unsicher: (r) => ((r.with_value ?? 0) > 0 && (r.with_value ?? 0) < 20 ? 'wenig Anfos' : null),
      render: (r) =>
        nurVorlaeufig(r) ? (
          <Box sx={{ lineHeight: 1.25 }}>
            <Strich grund={`Alle Anfos sind vorläufig ergänzt. ${TERMS.vorlaeufig.tip}`} />
            <Unter>Wert noch unbekannt</Unter>
          </Box>
        ) : ohneWert(r) ? (
          // Kein Anfo-Wert für das Produkt (z. B. alte Einmalfaktura): „—“ statt 0 € (Konsistenzprüfung 29.10.).
          <Box sx={{ lineHeight: 1.25 }}>
            <Strich grund="Für dieses Produkt gibt es keinen Anfo-Wert (z. B. eine frühere Einmalfaktura) — der Erwartete Wert ist unbekannt, nicht 0 €." />
            <Unter>kein Anfo-Wert</Unter>
          </Box>
        ) : (
          // Summe und Ø untereinander (Regel 1): nebeneinander war die Zelle bei 267 px nicht trennbar.
          <Box sx={{ lineHeight: 1.25 }}>
            {tEuro(r.value_sum ?? null)}
            <Unter>Ø {fest(formatEur(r.value_per_anfo ?? null, 0))} je Anfo</Unter>
          </Box>
        ),
      sortValue: (r) => (nurVorlaeufig(r) || ohneWert(r) ? null : (r.value_sum ?? null)),
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {tEuro(s.value_sum ?? null)}
          <Unter>Ø {fest(formatEur(s.value_per_anfo ?? null, 0))} je Anfo</Unter>
          {zwoelf && s.veraenderung_vorjahr && !s.veraenderung_vorjahr.neu && <Unter>{formatDelta(s.veraenderung_vorjahr.delta)} zum Vorjahr</Unter>}
        </Box>
      ) : null,
    },
    {
      key: 'ueber',
      label: TERMS.ueberWebinare.label,
      einheit: 'in %',
      tip: TERMS.ueberWebinare.tip,
      gruppe: 'Menge',
      align: 'right',
      render: (r) => {
        const n = r.anfos_fest ?? r.anfos - r.provisional;
        if (n <= 0) return <Strich grund="Keine Anfo mit bekanntem Kanal." />;
        return (
          <Box sx={{ lineHeight: 1.25, color: n < 20 ? 'text.disabled' : 'inherit' }}>
            {formatPct(((r.webinar_anfos_fest ?? 0) / n) * 100, 0)}
            <Unter>
              {int(r.webinar_anfos_fest)} von {int(n)}
            </Unter>
          </Box>
        );
      },
      sortValue: (r) => {
        const n = r.anfos_fest ?? r.anfos - r.provisional;
        return n > 0 ? (r.webinar_anfos_fest ?? 0) / n : null;
      },
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {formatPct(((s.webinar_anfos_fest ?? 0) / Math.max(1, s.anfos_fest ?? 1)) * 100, 0)}
          <Unter>
            {int(s.webinar_anfos_fest)} von {int(s.anfos_fest)}
          </Unter>
        </Box>
      ) : null,
    },
    {
      key: 'quote',
      label: TERMS.bezahlquote.label,
      einheit: `Anfos ab ${60} Tagen`,
      tip: spaltenTip('bezahlquote', `Unter 20 reifen Anfos grau.${zwoelf ? '' : ' ▲/▼ nur im Zeitraum „12 Monate“: Die Bezahlquote ändert sich über die Zeit, über mehrere Jahre läge ein Produkt, das vor allem früh lief, allein dadurch über dem Schnitt.'}`),
      hinweis: TERMS.abweichungMarke.tip,
      gruppe: 'Zahlung (wächst mit dem Alter)',
      align: 'right',
      render: (r) => {
        if (nurVorlaeufig(r)) return <Strich grund="Zahlstand unbekannt: alle Anfos sind vorläufig ergänzt." />;
        if (r.pay_rate === null) return <Strich grund="Noch keine Anfo ist 60 Tage alt." />;
        const vorab = (r.anfos_fest ?? r.anfos) > 0 && r.upsell_anfos / (r.anfos_fest ?? r.anfos) >= VORAB_ANTEIL;
        const quote = <Quote r={r} />;
        return vorab ? (
          <Tooltip title="Viele zahlen hier den Jahresbezug vorab (hohe Upsell-Quote) — die Bezahlquote ist deshalb hoch." arrow placement="top">
            <Box component="span" sx={{ cursor: 'help', display: 'inline-block' }}>
              {quote}
            </Box>
          </Tooltip>
        ) : (
          quote
        );
      },
      sortValue: (r) => (nurVorlaeufig(r) ? null : r.pay_rate),
      unsicher: (r) => (r.pay_rate !== null && r.mature_anfos < 20 ? 'wenig reife Anfos' : null),
      summary: s ? <Quote r={{ ...s, pay_rate_flag: null }} /> : null,
    },
    {
      key: 'letztes',
      label: TERMS.letztesWebinar.label,
      tip: TERMS.letztesWebinar.tip,
      gruppe: 'Planung',
      align: 'right',
      render: (r) =>
        r.last_webinar ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {datum(r.last_webinar)}
            {/* B15/B32: ab heute gezählt, ohne „vor 0 Wochen" / „vor 1 Wochen". */}
            {r.rest_days !== null && <Unter>{vorWochen(r.rest_days)}</Unter>}
          </Box>
        ) : (
          <Strich grund="Kein Webinar dieses Produkts im Zeitraum." />
        ),
      sortValue: (r) => r.last_webinar,
    },
    {
      key: 'veraenderung',
      label: TERMS.veraenderungVorjahr.label,
      tip: TERMS.veraenderungVorjahr.tip,
      hinweis: TERMS.imVorjahrErstAb.tip,
      gruppe: 'Trend',
      align: 'right',
      unsicher: (r) => (r.vorjahr_erst_ab ? 'Anlauf im Vorjahr' : r.veraenderung_vorjahr?.grau_grund ? 'wenig Anfos' : null),
      render: (r) =>
        r.veraenderung_vorjahr ? (
          <Box sx={{ lineHeight: 1.25 }}>
            <VeraenderungWert v={r.veraenderung_vorjahr} schwelle={schwelle} />
            {r.vorjahr_erst_ab && <Unter>im Vorjahr erst ab {mmjj(r.vorjahr_erst_ab)}</Unter>}
            {r.nachfolger && <Unter>Nachfolger {r.nachfolger}</Unter>}
          </Box>
        ) : (
          '–'
        ),
      sortValue: (r) => r.veraenderung_vorjahr?.delta ?? null,
    },
    {
      key: 'bestand',
      label: TERMS.bestandskundenOhneProdukt.label,
      tip: TERMS.bestandskundenOhneProdukt.tip,
      gruppe: 'Kundentyp',
      align: 'right',
      render: (r) => int(plan.bestand.get(r.plx_product) ?? null),
      sortValue: (r) => plan.bestand.get(r.plx_product) ?? null,
    },
    {
      key: 'erst',
      label: TERMS.anteilErstbesteller.label,
      tip: zwoelf ? TERMS.anteilErstbesteller.tip : `${TERMS.anteilErstbesteller.tip} ${TERMS.historieAb2024.tip}`,
      gruppe: 'Kundentyp',
      align: 'right',
      render: (r) =>
        zwoelf ? (
          formatPct(r.first_buyer_share ?? null, 0)
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct(r.first_buyer_share ?? null, 0)}
            <Unter>{TERMS.historieAb2024.short}</Unter>
          </Box>
        ),
      sortValue: (r) => r.first_buyer_share ?? null,
    },
    {
      // E3: Ø Listenpreis (netto, nur außerhalb von 12 Monaten geliefert) mit dem Anteil geschätzter Preise.
      key: 'preis',
      label: `Ø ${TERMS.listenpreis.label}`,
      einheit: 'netto · Anteil geschätzt',
      tip: spaltenTip('listenpreis', `Netto: brutto geteilt durch den MwSt.-Faktor des Produkts. Darunter der Anteil der Anfos mit heutigem statt damaligem Preis.`),
      gruppe: 'Wert',
      align: 'right',
      render: (r) =>
        r.avg_list_price_net === undefined || r.avg_list_price_net === null ? (
          <Strich grund={zwoelf ? 'Nur in den Zeiträumen „seit 2025“ und „gesamt“.' : 'Keine Anfo mit Listenpreis.'} />
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatEur(r.avg_list_price_net, 0)}
            <Unter>{formatPct(r.list_price_estimated_share, 0)} geschätzt</Unter>
          </Box>
        ),
      sortValue: (r) => r.avg_list_price_net ?? null,
      summary: s && s.avg_list_price_net != null ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {formatEur(s.avg_list_price_net, 0)}
          <Unter>{formatPct(s.list_price_estimated_share, 0)} geschätzt</Unter>
        </Box>
      ) : null,
    },
    {
      key: 'upq',
      label: TERMS.upsellQuote.label,
      tip: TERMS.upsellQuote.tip,
      gruppe: 'Kundentyp',
      align: 'right',
      render: (r) => {
        const n = r.anfos_fest ?? r.anfos - r.provisional;
        return n > 0 ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct((r.upsell_anfos / n) * 100)}
            <Unter>
              {int(r.upsell_anfos)} von {int(n)}
            </Unter>
          </Box>
        ) : (
          '–'
        );
      },
      sortValue: (r) => ((r.anfos_fest ?? 0) > 0 ? r.upsell_anfos / (r.anfos_fest as number) : null),
    },
    {
      key: 'webinare',
      label: 'Webinare',
      tip: `Gelaufene Termine des Produkts im Zeitraum (${z.satz}, ohne Dauer-Webinare). Webinare zählen beim beworbenen Produkt, Anfos beim bestellten Artikel.`,
      gruppe: 'Menge',
      align: 'right',
      render: (r) => int(r.webinars_window),
      sortValue: (r) => r.webinars_window,
      summary: int(s?.webinars_window),
    },
    {
      key: 'wjt',
      label: TERMS.wertJeTerminTypisch.label,
      tip: TERMS.wertJeTerminTypisch.tip,
      gruppe: 'Wert',
      align: 'right',
      render: (r) => tEuro(r.wert_je_termin_typisch ?? null),
      sortValue: (r) => r.wert_je_termin_typisch ?? null,
    },
    {
      key: 'einzel',
      label: TERMS.einzelwertNetto.label,
      tip: TERMS.einzelwertNetto.tip,
      gruppe: 'Wert',
      align: 'right',
      render: (r) => formatEur(r.anfo_wert, 0),
      sortValue: (r) => r.anfo_wert,
    },
    {
      key: 'paid',
      label: TERMS.umsatzNetto.label,
      einheit: 'bezahlte Anfos · Ø Alter',
      tip: TERMS.bezahltJeAnfo.tip,
      gruppe: 'Zahlung (wächst mit dem Alter)',
      nurMit: 'wert',
      align: 'right',
      render: (r) => <BezahltBuendel wert={formatEur(r.paid_revenue, 0)} zahler={r.payers ?? 0} alterTage={r.avg_age_days ?? null} />,
      sortValue: (r) => r.paid_revenue,
      summary: s ? <BezahltBuendel wert={formatEur(s.paid_revenue, 0)} zahler={s.payers ?? 0} alterTage={s.avg_age_days ?? null} /> : null,
    },
    {
      key: 'paidJe',
      label: TERMS.bezahltJeAnfo.label,
      einheit: 'Ø Alter',
      tip: TERMS.bezahltJeAnfo.tip,
      gruppe: 'Zahlung (wächst mit dem Alter)',
      nurMit: 'wert',
      align: 'right',
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {formatEur(r.paid_per_anfo, 0)}
          <Unter>{r.avg_age_days === null || r.avg_age_days === undefined ? '–' : `Ø ${int(r.avg_age_days)} Tage alt`}</Unter>
        </Box>
      ),
      sortValue: (r) => r.paid_per_anfo,
    },
  ];
  // E3: außerhalb von 12 Monaten keine Veränderung zum Vorjahr und kein typischer Wert je Termin (die Webinar-Basis reicht
  // nur 12 Monate zurück); der Ø Listenpreis steht dann im Standard. In 12 Monaten gibt es ihn nicht (Antwort unverändert).
  const NUR_12M = new Set(['veraenderung', 'wjt']);
  const passt = (c: Spalte<ProduktZeile>) => (zwoelf ? c.key !== 'preis' : !NUR_12M.has(c.key));
  const meta = nachGruppenReihenfolge(spaltenMeta(baue().filter(passt)), GRUPPEN);
  const wahl = useSpaltenwahl('produkte', meta, STANDARDS);
  // Kein Erzwingen von 'preis' mehr: das Menü zeigte die Spalte sonst als abgewählt („6 von 14“), obwohl sie dastand,
  // und das Abwählen wirkte nicht (Abnahme 29.10.). Sie steht jetzt in STANDARDS.
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(wahl.breite === 'breit').filter((c) => passt(c) && sichtbar.has(c.key));
  const quoteSichtbar = sichtbar.has('quote');

  return (
    <ChartCard
      title={z.titel}
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={TERMS.produkteZeitraum.tip} arrow placement="top">
            <ToggleButtonGroup size="small" exclusive value={zeitraum} onChange={(_e, v) => v && setZeitraum(v)} aria-label={TERMS.produkteZeitraum.label}>
              {ZEITRAEUME.map((x) => (
                <ToggleButton key={x.key} value={x.key} sx={{ textTransform: 'none', py: 0.25 }}>
                  {x.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Tooltip>
          <SpaltenMenue spalten={meta} wahl={wahl} />
        </Box>
      }
      howToRead="Je Produkt: wie viele Anfos es gebracht hat, was sie erwarten lassen (Summe und je Anfo) und welcher Teil über Webinare kam. Sortiert nach dem Erwarteten Wert — er ist über das Alter vergleichbar, „bisher bezahlt“ nicht. Nur ▲/▼ sind auffällig: über bzw. unter dem Schnitt, statistisch geprüft. Einmalkäufe und ausgelaufene Produkte stehen am Ende."
      terms={[
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
        { label: TERMS.bezahlquote.label, tip: TERMS.bezahlquote.tip },
        { label: TERMS.abweichungMarke.label, tip: TERMS.abweichungMarke.tip },
        { label: ATTRIBUTION.bannerKanal.label, tip: ATTRIBUTION.bannerKanal.tip },
        { label: TERMS.produkteZeitraum.label, tip: TERMS.produkteZeitraum.tip },
        ...(zwoelf
          ? []
          : [
              { label: TERMS.listenpreis.label, tip: TERMS.listenpreis.tip },
              { label: TERMS.historieAb2024.label, tip: TERMS.historieAb2024.tip },
            ]),
        ...((s?.wert_geschaetzt_anfos ?? 0) > 0 ? [{ label: TERMS.wertGeschaetzt.label, tip: TERMS.wertGeschaetzt.tip }] : []),
        ...((s?.provisional ?? 0) > 0 ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
      ]}
      source="PL-X-Bestellungen und Zahlungen, Webinar-Termine, Anfo-Wert je Anfo"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {/* B5: Wert je Anfo des stärksten Produkts gegen den Schnitt, dazu die geprüften ▲/▼ der Bezahlquote. */}
            <BefundZeile text={befund} fehlt={befund ? null : 'Zu wenige Produkte mit mindestens 20 Anfos mit Wert.'} tip={`Ø je Anfo = ${TERMS.wertBeiBestellung.label}. ${TERMS.abweichungMarke.tip}`} />
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* B50: Ende = letzter eingeschlossener Tag (Datenstand), nicht der Stichtag. */}
              Bestellungen {spanne(data.window.von, data.vergleich?.jetzt.letzter_tag ?? data.window.asOf)} · {TERMS.wertSumme.label} {tEuro(s?.value_sum ?? null)}
              {zwoelf && s?.veraenderung_vorjahr && !s.veraenderung_vorjahr.neu && `, ${formatDelta(s.veraenderung_vorjahr.delta)} zum Vorjahr`}
              {zwoelf && s?.veraenderung_vorjahr && data.vergleich && (
                <InfoTip text={`${veraenderungTip(s.veraenderung_vorjahr, schwelle)} ${datenstandSatz(data.vergleich)} ${TERMS.veraenderungVorjahr.tip}`} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Legende
                items={[
                  { label: 'über Webinare', color: viz.series1 },
                  { label: 'andere Kanäle', color: viz.series2 },
                ]}
              />
              <ToggleButtonGroup size="small" exclusive value={ansicht} onChange={(_e, v) => v && setAnsicht(v)} aria-label="Grafik zeigt">
                <ToggleButton value="wert" sx={{ textTransform: 'none', py: 0.25 }}>
                  {TERMS.wertBeiBestellung.short}
                </ToggleButton>
                <ToggleButton value="anfos" sx={{ textTransform: 'none', py: 0.25 }}>
                  {TERMS.anfos.label}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ height: balkenHoehe(chartData.length) }}>
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={['webinar', 'andere']}
                indexBy="produkt"
                layout="horizontal"
                margin={{ top: 4, right: breitBild ? 72 : 56, bottom: 36, left: links }}
                padding={0.3}
                innerPadding={2}
                borderRadius={4}
                colors={[viz.series1, viz.series2]}
                theme={viz.nivo}
                animate={false}
                enableGridY={false}
                enableGridX
                gridXValues={breitBild ? 5 : 2}
                enableLabel={false}
                axisLeft={{ tickSize: 0, tickPadding: 8, renderTick: (t) => (
                    <AchsenName
                      tick={t}
                      text={achsenText.get(String(t.value)) ?? String(t.value)}
                      voll={achsenVoll.get(String(t.value))}
                      farbe={viz.inkSecondary}
                      fontFamily={viz.fontFamily}
                    />
                  ),
                }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: breitBild ? 5 : 2, format: ansicht === 'wert' ? (v) => `${Math.round(Number(v) / 1000)} T€` : achse() }}
                layers={[
                  'grid',
                  'axes',
                  'bars',
                  ({ bars }) => {
                    const ende = new Map<string, { x: number; y: number; h: number; v: number }>();
                    for (const b of bars) {
                      const key = String(b.data.indexValue);
                      const e = ende.get(key);
                      const x = b.x + b.width;
                      if (!e || x > e.x) ende.set(key, { x, y: b.y, h: b.height, v: Number(b.data.data.gesamt) });
                    }
                    return (
                      <g>
                        {[...ende.entries()].map(([k, e]) => (
                          <text key={k} x={e.x + 6} y={e.y + e.h / 2} dominantBaseline="central" fontSize={12} fontFamily={viz.fontFamily} fill={viz.inkSecondary}>
                            {ansicht === 'wert' ? tEuro(e.v) : int(e.v)}
                          </text>
                        ))}
                      </g>
                    );
                  },
                ]}
                tooltip={({ data: d }) => (
                  <Box>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      {String(d.produkt)} · {String(d.name)}
                    </Box>
                    <TipRow color={viz.series1} label="über Webinare" value={ansicht === 'wert' ? tEuro(Number(d.webinar)) : int(Number(d.webinar))} />
                    <TipRow color={viz.series2} label="andere Kanäle" value={ansicht === 'wert' ? tEuro(Number(d.andere)) : int(Number(d.andere))} />
                    <TipRow label="zusammen" value={ansicht === 'wert' ? tEuro(Number(d.gesamt)) : int(Number(d.gesamt))} />
                  </Box>
                )}
                role="img"
                ariaLabel={`${ansicht === 'wert' ? 'Erwarteter Wert' : 'Anfos'} je Produkt, ${z.satz}, getrennt nach Webinar und anderen Kanälen`}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SortableTable
                rows={data.rows}
                columns={spalten}
                rowKey={(r) => r.plx_product || '(ohne)'}
                initialSort={{ key: 'wert', dir: 'desc' }}
                maxHeight={null}
                eng={wahl.breite !== 'breit'}
                abschnitt={(r) => (imAbschnitt(r) ? 'Einmalkäufe und ausgelaufene Produkte' : null)}
                namensEtikett={(r) => (
                  <>
                    {/* B28: ohne sichtbare Spalte (Handy) sagt der Pfeil allein nicht, WAS abweicht — Touch hat kein title. */}
                    {!quoteSichtbar && r.pay_rate_flag && (
                      <NamensEtikett tip={`${r.pay_rate_flag === 'up' ? 'Zahlt auffällig besser' : 'Zahlt auffällig schlechter'} als der Schnitt. ${TERMS.abweichungMarke.tip}`}>
                        {`${TERMS.bezahlquote.label} ${r.pay_rate_flag === 'up' ? '▲' : '▼'}`}
                      </NamensEtikett>
                    )}
                    {plan.geplant.has(r.plx_product) && (
                      <NamensEtikett tip="Für den nächsten Sonntag ist ein Termin dieses Produkts angelegt.">{`geplant ${tagMitWochentag(plan.geplant.get(r.plx_product))}`}</NamensEtikett>
                    )}
                    {!plan.geplant.has(r.plx_product) && plan.frei.has(r.plx_product) && (
                      <NamensEtikett tip="Das Produkt lief kürzlich; die Empfehlung schlägt es erst ab diesem Sonntag wieder vor.">{`frei ab ${tagMonat(plan.frei.get(r.plx_product))}`}</NamensEtikett>
                    )}
                    {r.nachfolger && <NamensEtikett tip={TERMS.keinWebinarMehr.tip}>{`${TERMS.keinWebinarMehr.short}, Nachfolger ${r.nachfolger}`}</NamensEtikett>}
                    {r.ist_einmalkauf && <NamensEtikett tip="Einmalkauf = Artikel ohne Abo.">Einmalkauf</NamensEtikett>}
                    <GeschaetztMarke anfos={r.wert_geschaetzt_anfos} />
                  </>
                )}
                summaryLabel="Alle Anfos"
              />
            </Box>
            <Hinweis>
              Euro netto. Bezahlquote nur über Anfos ab 60 Tagen. Webinare zählen beim beworbenen Produkt, Anfos beim bestellten Artikel. Einmalkauf
              und „kein Webinar mehr“ sind feste Listen, keine Zeitregel. {vorlaeufigSatz(s?.provisional ?? 0)}
              {!zwoelf &&
                ` Zeitraum ${z.satz}: ohne Veränderung zum Vorjahr und ohne typischen Wert je Termin; Listenpreise sind für ältere Anfos überwiegend geschätzt (heutiger statt damaliger Preis), der Erstbesteller-Anteil hat Kunden-Historie erst ab 2024.`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

/** Bezahlquote mit Nenner und ▲/▼ (Marke nur, solange die Spalte sichtbar ist; sonst am Namen). */
const Quote: FC<{ r: Pick<ProduktZeile, 'pay_rate' | 'mature_paid' | 'mature_anfos' | 'pay_rate_flag'> }> = ({ r }) =>
  r.pay_rate === null ? (
    <>–</>
  ) : (
    <Box sx={{ lineHeight: 1.25 }}>
      {formatPct(r.pay_rate)}
      <AbweichungMarke marke={r.pay_rate_flag} />
      <Unter>
        {int(r.mature_paid)} von {int(r.mature_anfos)}
      </Unter>
    </Box>
  );

export default ProdukteKarte;
